import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { TestUsersProvisionCard } from './TestUsersProvisionCard.jsx';

// ─── Dashboard (main view) ────────────────────────────────────────────────────
// ─── Admin Toggles (Round 38) ───────────────────────────────────────────
// Generic key/value settings panel. Today's only entry is the
// post-certify Square mirror toggle. Add more rows as features need
// global on/off knobs.
export function AdminTogglesView({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(null);
  const [err, setErr] = useState('');

  async function load() {
    try {
      const r = await api('GET', '/api/admin/settings', null, headers);
      setSettings(r.settings || {});
    } catch (e) { setErr(e.message || String(e)); }
  }
  useEffect(() => { load(); }, []);

  async function setOne(key, value) {
    setSaving(key); setErr('');
    try {
      await api('PUT', `/api/admin/settings/${key}`, { value }, headers);
      setSettings(s => ({ ...s, [key]: value }));
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally { setSaving(null); }
  }

  const toggles = [
    {
      key: 'square_mirror_after_cert',
      label: 'Mirror certified timecards to Square Labor',
      desc: "When ON, every certify-week action also creates Square Labor shift records as a backup. Leave OFF until Monarch is your payroll source-of-truth — then flip it on so you have a parallel record in Square for redundancy.",
    },
    {
      key: 'knowledge_drip_enabled',
      label: 'Knowledge Drip cards (retired)',
      desc: "Retired 2026-08-01 — OFF, and the stored card history was purged. When ON, each person gets one bite-size knowledge card per day on their Home screen and at the kiosk after clocking in, and Monarch records which cards they saw and whether they answered correctly. PRIVACY: that per-person record is a second individual-level record, and the staff-facing Privacy Commitments policy currently promises policy acknowledgments are the ONLY one. Turning this back ON makes that published policy false — amend and re-publish it FIRST (docs/PRIVACY-AMENDMENT-DRIP-DRAFT.md has the drafted language).",
    },
    {
      key: 'reliability_task_completion',
      label: 'Count task completion in reliability scores',
      desc: "When ON, each person's task-completion rate over the rolling window becomes a component of the reliability score on their profile. When OFF (default), the score is based on attendance and timeclock incidents only.",
    },
    {
      key: 'stock_board_enabled',
      label: '86 board on the store kiosks',
      desc: "When ON, each store kiosk gets an \u201c86\u201d tab: staff mark items out with a reason (Square shows them sold out online), restore them with a count, and answer an opening prompt for anything still out. Leave OFF until the stock reset is done \u2014 on 2026-10-04 Square showed 94 of 140 tracked items sold out, and the board would open every store's day with that list.",
    },
    {
      key: 'store_check_scheduled_enabled',
      label: 'Store Check: scheduled checks',
      desc: "When ON, Monarch asks every store's camera for a still every 15 minutes while the stores are open, the AI reads each one, and a day report is written after 6:30pm. When OFF, a check happens only when an owner or manager presses Check now. Leave OFF until staff have been told in writing that the cameras are used this way.",
    },
  ];

  return (
    <div className="space-y-3">
      <div className="card">
        <div className="text-base font-semibold">Admin toggles</div>
        <div className="text-xs text-gray-500">Global feature flags. Changes take effect immediately.</div>
      </div>
      {err && <div className="card" style={{ borderLeft: '4px solid #c62828' }}><div className="text-xs text-red-700">{err}</div></div>}
      {toggles.map(t => {
        // Rows default OFF unless the toggle opts into defaultOn, in which case
        // absence/any-non-'false' reads as ON — matching the server's `value !== 'false'` check.
        const raw = settings[t.key];
        const on = (raw === undefined || raw === null) ? !!t.defaultOn : raw !== 'false';
        return (
          <div key={t.key} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#212121' }}>{t.label}</div>
              <div style={{ fontSize: 12, color: '#757575', marginTop: 4 }}>{t.desc}</div>
            </div>
            <button onClick={() => setOne(t.key, on ? 'false' : 'true')} disabled={saving === t.key}
              style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700,
                        background: on ? 'var(--forest, #1B2B1B)' : '#e0e0e0',
                        color: on ? '#fff' : '#37474f',
                        border: 'none', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                        minWidth: 80 }}>
              {saving === t.key ? '…' : on ? 'ON' : 'OFF'}
            </button>
          </div>
        );
      })}

      {/* Provision the two manual-screenshot test accounts. Owner-only
          server-side; idempotent. CJ 2026-05-14. */}
      <TestUsersProvisionCard headers={headers} />
    </div>
  );
}
